import type { ICitiesAPI, ICity, TGetCitiesResponse } from '../models';

import { EDeliveryType } from '../models';
import { CITIES_CONFIG } from './config';

import { ELocalStorageKeys } from '../../ELocalStorageKeys';
import { useLocalStorage } from '@vueuse/core';

export class _CitiesStore {
  private readonly cities = ref<ICity[]>([]);
  public readonly currentCityId: Ref<number>;

  private citiesUpdatingPromise: Promise<TGetCitiesResponse>;
  private isPending = false;

  #ApiService: ICitiesAPI;

  constructor(apiService: ICitiesAPI) {
    this.#ApiService = apiService;

    this.currentCityId = useLocalStorage(
      ELocalStorageKeys.MM__CITIES_STORE__CURRENT_CITY_ID,
      CITIES_CONFIG.FALLBACK_CITY.id,
      { listenToStorageChanges: false }
    );

    this.citiesUpdatingPromise = Promise.resolve({ payload: this.cities.value });
  }

  public getCities() {
    const isNeedToUpdate = !this.cities.value.length;

    if (isNeedToUpdate && !this.isPending) {
      this.updateCities();
    }

    return this.cities;
  }

  public async getCitiesAsync() {
    this.getCities();
    await this.citiesUpdatingPromise;
    return this.cities.value;
  }

  private async updateCities() {
    this.isPending = true;
    this.citiesUpdatingPromise = this.#ApiService.updateCities();

    return this.citiesUpdatingPromise
      .then((result) => {
        this.cities.value = result.payload;
        /** если в полученном списке отсутствует currentCity => меняем на fallbackCity */
        this.currentCityId.value = this.getCurrentCity().id;
      })
      .finally(() => {
        this.isPending = false;
      });
  }

  public setCurrentCityId(id: string) {
    this.currentCityId.value = Number.parseInt(id);
  }

  public hasCurrentCityCourierDelivery() {
    const deliveryTypes = this.getCurrentCity().deliveryTypes;

    return deliveryTypes.includes(EDeliveryType.COURIER);
  }

  public getCurrentCity() {
    const currentCity = this.cities.value.find((city) => {
      return city.id.toString() === this.currentCityId.value.toString();
    });

    return currentCity ?? CITIES_CONFIG.FALLBACK_CITY;
  }

  public getCurrentCityId() {
    return this.currentCityId.value.toString();
  }

  /** Убрать после того, как бек начнет отдавать координаты центра */
  public getCurrentCityCenterCoord() {
    const deliveryPoints = this.getCurrentCity().deliveryPoints;

    const deliveryPointsFiltered = deliveryPoints.filter((dp) => {
      return dp.longitude && dp.latitude;
    });

    if (!deliveryPointsFiltered.length) return CITIES_CONFIG.FALLBACK_CENTER_COORD;

    const coordSum = [0, 0];

    deliveryPointsFiltered.forEach((dp) => {
      coordSum[0] += dp.longitude;
      coordSum[1] += dp.latitude;
    });

    const longitude = coordSum[0] / deliveryPointsFiltered.length;
    const latitude = coordSum[1] / deliveryPointsFiltered.length;

    return [longitude, latitude];
  }
}
